import { z } from "zod";

import {
  FormInputItem,
  LooseObject,
  MessageText,
  PasswordLength,
} from "~/utils/Types";
import { isEmail } from "~/utils/zodValidators";

const getValidateOptions = (fieldsWithContext: FormInputItem[]) => {
  const options: LooseObject = {};
  if (
    fieldsWithContext &&
    Array.isArray(fieldsWithContext) &&
    fieldsWithContext.length > 0
  ) {
    const fields = fieldsWithContext.map(
      (item) => !item.avoidValidation && item.name,
    );
    if (fields.includes("name")) {
      options.name = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("firstName")) {
      options.firstName = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("lastName")) {
      options.lastName = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("email")) {
      options.email = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED)
        .refine((v) => isEmail(v), MessageText.INPUT_ERROR_EMAIL);
    }
    if (fields.includes("password")) {
      options.password = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED)
        .min(
          PasswordLength.MIN,
          `At least ${PasswordLength.MIN} characters please`,
        )
        .max(
          PasswordLength.MAX,
          `No more than ${PasswordLength.MAX} characters please`,
        );
    }
    if (fields.includes("oldPassword")) {
      options.oldPassword = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED)
        .min(
          PasswordLength.MIN,
          `At least ${PasswordLength.MIN} characters please`,
        )
        .max(
          PasswordLength.MAX,
          `No more than ${PasswordLength.MAX} characters please`,
        );
    }
    if (fields.includes("passwordAgain")) {
      options.passwordAgain = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED)
        .min(
          PasswordLength.MIN,
          `At least ${PasswordLength.MIN} characters please`,
        )
        .max(
          PasswordLength.MAX,
          `No more than ${PasswordLength.MAX} characters please`,
        );
    }
    if (fields.includes("idString")) {
      options.idString = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("zitadelOrgIdString")) {
      options.zitadelOrgIdString = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("displayName")) {
      options.displayName = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("country")) {
      options.country = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    // if (fields.includes("pages")) {
    //   options.pages = Yup.string()
    //     .required(MessageText.INPUT_ERROR_REQUIRED)
    //     .test("isValidJSON", MessageText.INPUT_ERROR_JSON, v => validator.isJSON(v));
    // }
    if (fields.includes("roleKey")) {
      options.roleKey = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("roleDescription")) {
      options.roleDescription = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("pic")) {
      options.pic = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("businessName")) {
      options.businessName = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("quantity")) {
      options.quantity = z.union([
        z.number().min(0.01, MessageText.INPUT_ERROR_REQUIRED),
        z
          .string()
          .min(1, MessageText.INPUT_ERROR_REQUIRED)
          .transform((val, ctx) => {
            const parsed = Number(val);
            if (isNaN(parsed) || !isFinite(parsed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MessageText.INPUT_ERROR_NUMBER,
              });
              return z.NEVER;
            }
            return parsed;
          }),
      ]);
    }
    if (fields.includes("status")) {
      options.status = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("bookingDate")) {
      options.bookingDate = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("gridPricingYear")) {
      options.gridPricingYear = z
        .number()
        .min(2020, `Min 2020`)
        .max(2050, `Max 2050`);
    }
    if (fields.includes("gridPricingWeek")) {
      options.gridPricingWeek = z.number().min(1, `Min 1`).max(52, `Max 52`);
    }
    if (fields.includes("agreedQuantity")) {
      options.agreedQuantity = z.union([
        z.number().min(0.01, MessageText.INPUT_ERROR_REQUIRED),
        z
          .string()
          .min(1, MessageText.INPUT_ERROR_REQUIRED)
          .transform((val, ctx) => {
            const parsed = Number(val);
            if (isNaN(parsed) || !isFinite(parsed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: MessageText.INPUT_ERROR_NUMBER,
              });
              return z.NEVER;
            }
            return parsed;
          }),
      ]);
    }
    if (fields.includes("breed")) {
      options.breed = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("estimatedAverageWeightKg")) {
      options.estimatedAverageWeightKg = z.coerce
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("woolLengthMm")) {
      options.woolLengthMm = z.coerce
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("finishingNutrition")) {
      options.finishingNutrition = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("ageMonths")) {
      options.ageMonths = z.coerce
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isMuelsed")) {
      options.isMuelsed = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isMsaAccredited")) {
      options.isMsaAccredited = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("msaNumber")) {
      options.msaNumber = z.string().optional();
    }
    if (fields.includes("isGapCertified")) {
      options.isGapCertified = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isGrassFed")) {
      options.isGrassFed = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isFreeRange")) {
      options.isFreeRange = z.string().min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isComplyWithLPA")) {
      options.isComplyWithLPA = z.boolean().refine((val) => val === true, {
        message: MessageText.INPUT_ERROR_REQUIRED,
      });
    }
    if (fields.includes("isAntibioticFree")) {
      options.isAntibioticFree = z.boolean().refine((val) => val === true, {
        message: MessageText.INPUT_ERROR_REQUIRED,
      });
    }
    if (fields.includes("isHormoneAndGrowthPromotantFree")) {
      options.isHormoneAndGrowthPromotantFree = z
        .boolean()
        .refine((val) => val === true, {
          message: MessageText.INPUT_ERROR_REQUIRED,
        });
    }
    if (fields.includes("picOfAutocomplete")) {
      options.picOfAutocomplete = z.custom<LooseObject>(
        (val) => {
          if (typeof val === "string") return val.trim().length > 0;
          if (val && typeof val === "object" && "value" in val) {
            const v = (val as LooseObject).value;
            return typeof v === "string" && v.trim().length > 0;
          }
          return false;
        },
        { message: MessageText.INPUT_ERROR_REQUIRED },
      );
    }
    if (fields.includes("producerFirstName")) {
      options.producerFirstName = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("producerLastName")) {
      options.producerLastName = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("producerBusinessName")) {
      options.producerBusinessName = z
        .string()
        .min(1, MessageText.INPUT_ERROR_REQUIRED);
    }
    if (fields.includes("isTocAgreed")) {
      options.isTocAgreed = z.boolean().refine((val) => val === true, {
        message: MessageText.INPUT_ERROR_REQUIRED,
      });
    }
  }

  return options;
};

// Returns a plain zod schema — TanStack Form consumes it directly as a
// Standard Schema validator (issue paths map onto the matching fields).
// Unknown keys pass through untouched: only the fields declared above are
// validated, same as the Formik-era behaviour.
const getValidationSchema = (fields: FormInputItem[]) => {
  const options = getValidateOptions(fields);
  const schema = z.object(options).passthrough();

  // Handle passwordAgain field dependency if both password and passwordAgain are present
  if (
    fields.some((f) => f.name === "password") &&
    fields.some((f) => f.name === "passwordAgain")
  ) {
    return schema.refine((data) => data.password === data.passwordAgain, {
      message: "Password does not match",
      path: ["passwordAgain"],
    });
  }

  return schema;
};

export default getValidationSchema;
